/* tslint:disable:import-spacing component-class-suffix no-inferrable-types variable-name
   typedef-whitespace component-selector no-trailing-whitespace */

import {
  Component,
  AfterViewInit,
  Input,
  HostListener
} from '@angular/core';

import { DecisionPoint  } from '@/models/scenario/decision.point';
import { Track          } from '@/models/scenario/track';
import { Scenario       } from '@/models/scenario/scenario';
import { Logger         } from '@/util/logger';

import 'ag-grid-community';

import {StatusReporter} from "@/util/status.reporter";
import {Grid, GridApi} from "ag-grid-community";
import {ConnectionViewerMetadata} from "@/screens/ide/connection.viewer.metadata";
import {IdeService} from "@/services/ide.service";

@Component({
  selector: 'plant-table',
  template: `
    <div id="plant-table-container_{{uuid}}" class="plant-table-container" style="">
      <ag-grid-angular id="ag-grid-plant-table_{{uuid}}" style="width: 100%; height: 100%;"
        class="ag-theme-balham-dark ag-grid-plant-table"
        [rowData]="rowData"
        [columnDefs]="columnDefs"
        [defaultColDef]="defaultColDef"
        [context]="context"
        [animateRows]="true"
        [enableRangeSelection]="true"

        [allowContextMenuWithControlKey]="true"
        [getContextMenuItems]="getContextMenuItems"

        (gridReady)="onGridReady($event)"
      >
      </ag-grid-angular>
</div>
    `
})

/**
 *
 * @class PlantTable
 *
 * @brief this table shows the master plant
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      October 2019
 *
 */
export class PlantTable implements AfterViewInit {

  @Input() uuid: number                 = 0;

  /** @var _scenario the scenario model */
  private _scenario: Scenario           = null;

  /** the ag-grid widget */
  private         grid: Grid            = null;

  /** the API for the widget */
  private          api: GridApi         = null;

  private isFullScreen: boolean         = false;

  /** where we are sending our status messages */
  public statusReporter: StatusReporter = null;

  public context = {

    plantTable: this

  };

  public columnDefs: any[] = [

    {
      headerName: 'Decision Point',
      children: [
        {headerName: 'ROW',         field: 'row',      hide: true},
        {headerName: 'Mileage',     field: 'mileage',  editable: true, width: 150},
        {headerName: 'Name',        field: 'name',     editable: true, width: 150, columnGroupShow: 'open'},
        {
          headerName: 'Type',
          field: 'type',
          editable: true,
          width: 50,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: {
            values: ['S', 'D', 'P', 'T', 'J']
          },
          columnGroupShow: 'open'
        },
        {headerName: 'Layout',   field: 'layout',   width: 60,  columnGroupShow: 'open'},
        {headerName: 'Junction', field: 'junction', width: 60,  columnGroupShow: 'open'}
      ]
    }

    /* on init no track groups, we don't know how many there are yet */

  ];

  public defaultColDef = {
    sortable:  true,
    filter:    true,
    resizable: true
  };

  public rowData = [];

  /** standard constructor */
  constructor(
    private ide: IdeService
  ) {

    Logger.debug('[plant/table] dynamically creating...');

  }

  /** update the error line in the loader drop down */
  public success(msg: string =  '') {

    if (this.statusReporter === null) {

      return ;
    }

    this.statusReporter.success(msg);
  }

  /** update the error line in the loader drop down */
  public error(msg: string =  '') {

    if (this.statusReporter === null) {

      return ;
    }

    this.statusReporter.error(msg);
  }

  onGridReady(params) {
    this.grid          = params.grid;
    this.api           = params.api;
  }

  public toggleFullScreen() {


    if (this.isFullScreen) {

      Logger.debug('[plant/table]', 'exiting full screen...');

      document.exitFullscreen();

    } else {

      Logger.debug('[plant/table]', 'going full screen...');

      const elem = document.getElementById(`ag-grid-plant-table_${this.uuid}`);

      if (elem.requestFullscreen) {

        elem.requestFullscreen();
      }
    }

  }

  public getContextMenuItems(params) {

    const context: any[] = [];
    const gcPlantTable  = params.context.plantTable;

    if (params.value) {

      let dpLabel: string = params.value;

      if (params.value instanceof DecisionPoint) {

        dpLabel = params.value._name;
      }

      if (params.column.colDef.field === 'name') {

        context.push({
          name: `Connections...`,
          action: (): void => {

            gcPlantTable.ide.launchWindow(new ConnectionViewerMetadata(
              '',
              '',
              gcPlantTable._scenario,
              dpLabel
            ));
          },
          cssClasses: ['dpsrclaunch']
        });
      }

    }

    if (context.length !== 0) {

      context.push('separator');
    }

    /* full screen control */

    let label: string = 'Show Full Screen';

    if (gcPlantTable.isFullScreen) {

      label = 'Exit Full Screen';
    }

    context.push( {
      name: label,
      action: () => {

        gcPlantTable.toggleFullScreen();
      }
    });

    context.push('separator');

    const general: any[] = [

      'copy',
      'copyWithHeaders',
      'paste',

      'separator',

      'expandAll',
      'contractAll',

      'separator',

      'csvExport',
      'excelExport',
      'excelXmlExport',

      'separator',

      'autoSizeAll'
    ];

    /* pass back the merged menu */

    context.push.apply(context, general);

    return context;
  }

  /** @brief first time setup of view */
  ngAfterViewInit() {


    Logger.debug('[plant/table]', 'view init...');

    this.clear();

    const elem = document.getElementById(`ag-grid-plant-table_${this.uuid}`);

    const gcPlantTable = this;

    elem.addEventListener('fullscreenchange', () => {

      gcPlantTable.isFullScreen = document.fullscreenElement != null;

      Logger.debug('[plant/table]', '(elem) flipped full screen mode: ', gcPlantTable.isFullScreen);

    }, true);
  }

  /** @brief reset the view to empty */
  public clear() {

    this._scenario = null;
  }

  /** @brief standard destructor */
  destroy() {

    Logger.debug('[plant/table]', 'cleaning up...');

  }

  /** @brief set the table base on this scenario */
  setScenario(scenario: Scenario = null): boolean {

    if (scenario == null) {

      this.clear();

      return true;
    }

    /* save the model */

    this._scenario = scenario;

    /* get the plant report (the grid data) */

    const  report: any[]           = this._scenario.reportPlant();

    this.rowData           = [];
    this.columnDefs.length = 1;

    if (report.length === 0) {

      /* empty */

      this.api.setGridOption("columnDefs", this.columnDefs);
      this.api.setGridOption("rowData",    this.rowData);

      return true;
    }

    /* extend the columns to include enough space for the maximum possible track groups */

    const  maxTrack: number = report[0].length - 3;
    const trackNums: any[]  = [];

    for (let i = 1; i <= maxTrack; i++) {

      trackNums.push(i);
    }

    for (let i = 1; i <= maxTrack; i++) {

      this.columnDefs.push({
        headerName: 'Track #' + i,
          children: [
            {
              headerName: 'Type',
              field: 'type' + i,
              editable: true,
              width: 50,
              cellEditor: 'agSelectCellEditor',
              cellEditorParams: {
                values: ['.', 'M', 'P', 'J', 'T', 'U']
              }
            },
            {
              headerName: 'Track',
              field: 'track' + i,
              editable: true,
              width: 50,
              cellEditor: 'agSelectCellEditor',
              cellEditorParams: {
                values: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
              }
            },
            {headerName: 'Physical', field: 'physical' + i, width: 150, editable: true, columnGroupShow: 'open'},
            {headerName: 'Usable',   field: 'usable' + i,   width: 150, editable: true, columnGroupShow: 'open'},
            {headerName: 'Weight',   field: 'weight' + i,   width: 150, editable: true, columnGroupShow: 'open'}
          ]

      });
    }

    this.api.setGridOption('columnDefs', this.columnDefs);

    /* walk through the report, and create the row data */

    for (let i = 0; i < report.length; i++) {

      const row    = report[i];
      let layout   = '.';
      let junction = '.';

      if (maxTrack > 0) {

        layout   = row[3].layout;
        junction = row[3].junction;
      }

      /* use the layout/junction of the first track for the decision point */

      /* add basics */

      const rd = {
             row: i,
         mileage: row[0],
            name: row[1],
            type: row[2],
          layout,
         junction
      };

      /* now add in the track groups */

      for (let j = 1; j <= maxTrack; j++) {

        rd['type' + j]     = row[2 + j].type;
        rd['track' + j]    = row[2 + j].number;
        rd['physical' + j] = row[2 + j].length;
        rd['usable' + j]   = row[2 + j].useable;
        rd['weight' + j]   = row[2 + j].maxWeight;
      }

      this.rowData.push(rd);
    }

    this.api.setGridOption('rowData', this.rowData);

    /* this.gridColumnApi.autoSizeAllColumns(); */

    /* for now use horizontal scroll, table is getting really wide */
    /* this.gridApi.sizeColumnsToFit(); */

    /* all done */

    return true;
  }

  public focusOnDecisionPoint(dpId: number) {

    Logger.debug('[plant/table]', 'focusing on decision point: ' + dpId);

    /* find the decision point ... */

    const dps: DecisionPoint[] = this._scenario.milePoints(true);
    let  dp: DecisionPoint   = null;

    for (const dpItem of dps) {

      if (dpItem._id === dpId) {

        dp = dpItem;

        break;
      }

    }

    if (dp == null) {

      Logger.error('[plant/table]', 'no such decision point.');

      return ;
    }

    /* find the row for that decision point ... */

    this.api.forEachNode( (rowNode) => {

      if (rowNode.data.mileage !== dp._mileage) {

        return ;
      }

      /* select this node */

      rowNode.setSelected(true, true)

      /* make sure its visible */

      this.api.ensureNodeVisible(rowNode, 'top');

    });

  }

  @HostListener('window:resize')
  public resize() {

    Logger.debug('[plant/table]', 'resizing...');

    /* for now use horizontal scroll, table is getting really wide */
    /* this.gridApi.sizeColumnsToFit(); */
  }

}
